import { useEffect, useMemo, useRef, useState } from 'react';
import demoDeck from '../decks/demo.json';
import type { DeckDoc } from './types/slide-doc';
import type { PlayheadState } from './runtime/lessonPlayhead';
import { SlideView } from './components/SlideView';
import { primeAudio } from './runtime/stingerSynth';
import { preloadDeckImages } from './runtime/preload';
import { LessonPlayhead } from './runtime/lessonPlayhead';
import { ChapterRibbon } from './components/ChapterRibbon';
import { ScrubBar } from './components/ScrubBar';
import { SpeedControl } from './components/SpeedControl';
import { TapToContinue } from './components/TapToContinue';
import { RecordingHUD } from './components/RecordingHUD';

const DECK = demoDeck as DeckDoc;

type Mode = 'present' | 'play' | 'record';

export default function App() {
  const mode = useMemo<Mode>(() => {
    const url = new URLSearchParams(window.location.search);
    const m = url.get('mode');
    if (m === 'play' || m === 'record') return m;
    return 'present';
  }, []);

  useEffect(() => {
    preloadDeckImages(DECK);
  }, []);

  if (mode === 'play') return <PlayMode />;
  if (mode === 'record') return <RecordMode />;
  return <PresentMode />;
}

// ─── Present mode: current Phase-1 behaviour (slide-by-slide, no narration) ──

function PresentMode() {
  const [started, setStarted] = useState(false);
  const [index, setIndex] = useState(0);
  const [replay, setReplay] = useState(0);

  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'PageDown') {
        setIndex((i) => Math.min(i + 1, DECK.slides.length - 1));
      } else if (e.key === 'ArrowLeft' || e.key === 'PageUp') {
        setIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === 'r' || e.key === 'R') {
        setReplay((n) => n + 1);
      } else if (e.key === 'f' || e.key === 'F') {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen();
        else document.exitFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started]);

  useEffect(() => {
    if (started) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        primeAudio();
        setStarted(true);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started]);

  const next = () => setIndex((i) => Math.min(i + 1, DECK.slides.length - 1));
  const prev = () => setIndex((i) => Math.max(i - 1, 0));

  if (!started) return <Splash onStart={() => { primeAudio(); setStarted(true); }} />;
  const slide = DECK.slides[index];
  return (
    <>
      <SlideView key={`${slide.id}-${replay}`} slide={slide} />
      <button aria-label="Previous" onClick={prev} style={{ ...touchZone, left: 0, borderTopRightRadius: 18 }} />
      <button aria-label="Next" onClick={next} style={{ ...touchZone, right: 0, borderTopLeftRadius: 18 }} />
    </>
  );
}

// ─── Play mode: full lesson playback driven by LessonPlayhead ───────────

function PlayMode() {
  const [started, setStarted] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const playheadRef = useRef<LessonPlayhead | null>(null);
  const [state, setState] = useState<PlayheadState | null>(null);

  useEffect(() => {
    if (!started) return;
    const audio = DECK.narration ? new Audio(DECK.narration.src) : null;
    if (audio) {
      audio.preload = 'auto';
      audio.preservesPitch = true;
      audioRef.current = audio;
    }
    const ph = new LessonPlayhead(DECK, audio);
    playheadRef.current = ph;
    const unsub = ph.subscribe(setState);
    // Autoplay once user pressed start (gesture satisfied)
    ph.play();
    return () => {
      unsub();
      ph.destroy();
      if (audio) {
        audio.pause();
        audio.src = '';
      }
    };
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const onKey = (e: KeyboardEvent) => {
      const ph = playheadRef.current;
      if (!ph) return;
      if (e.key === ' ') {
        e.preventDefault();
        const s = ph.getState();
        if (s.pausedAtBeat) ph.tapContinue();
        else if (s.isPlaying) ph.pause();
        else ph.play();
      } else if (e.key === 'ArrowRight') {
        ph.seek(ph.getState().currentMs + 5000);
      } else if (e.key === 'ArrowLeft') {
        ph.seek(Math.max(0, ph.getState().currentMs - 5000));
      } else if (e.key === 'f' || e.key === 'F') {
        if (!document.fullscreenElement) document.documentElement.requestFullscreen();
        else document.exitFullscreen();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [started]);

  if (!started) {
    return <Splash subtitle="Play mode · lesson runtime" onStart={() => { primeAudio(); setStarted(true); }} />;
  }

  const ph = playheadRef.current;
  if (!ph || !state) return null;
  const activeSlide = DECK.slides.find((s) => s.id === state.activeSlideId) ?? DECK.slides[0];

  return (
    <>
      <SlideView slide={activeSlide} playhead={ph} />
      <ChapterRibbon deck={DECK} activeChapterId={state.activeChapterId} />
      <SpeedControl speed={state.speed} onChange={(x) => ph.setSpeed(x)} />
      <ScrubBar
        deck={DECK}
        playhead={ph}
        currentMs={state.currentMs}
        durationMs={state.durationMs}
        isPlaying={state.isPlaying}
        isPaused={!state.isPlaying}
      />
      {state.pausedAtBeat && <TapToContinue beat={state.pausedAtBeat} onTap={() => ph.tapContinue()} />}
    </>
  );
}

// ─── Record mode: capture narration while presenting ────────────────────

function RecordMode() {
  const [started, setStarted] = useState(false);
  const playheadRef = useRef<LessonPlayhead | null>(null);
  const [state, setState] = useState<PlayheadState | null>(null);

  useEffect(() => {
    if (!started) return;
    const ph = new LessonPlayhead(DECK, null);
    playheadRef.current = ph;
    const unsub = ph.subscribe(setState);
    return () => {
      unsub();
      ph.destroy();
    };
  }, [started]);

  if (!started) {
    return <Splash subtitle="Record mode · capture narration · output downloads as .webm" onStart={() => { primeAudio(); setStarted(true); }} />;
  }

  const ph = playheadRef.current;
  if (!ph || !state) return null;
  const activeSlide = DECK.slides.find((s) => s.id === state.activeSlideId) ?? DECK.slides[0];

  return (
    <>
      <SlideView slide={activeSlide} playhead={ph} />
      <ChapterRibbon deck={DECK} activeChapterId={state.activeChapterId} />
      <RecordingHUD deckId={DECK.id} onCancel={() => { window.location.search = ''; }} />
      <ScrubBar
        deck={DECK}
        playhead={ph}
        currentMs={state.currentMs}
        durationMs={state.durationMs}
        isPlaying={state.isPlaying}
        isPaused={!state.isPlaying}
      />
      {state.pausedAtBeat && <TapToContinue beat={state.pausedAtBeat} onTap={() => ph.tapContinue()} />}
    </>
  );
}

// ─── Splash component (shared) ──────────────────────────────────────────

function Splash({ onStart, subtitle }: { onStart: () => void; subtitle?: string }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        onStart();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onStart]);

  return (
    <div onClick={onStart} style={splashStyle}>
      <div style={{ fontSize: 64, fontWeight: 700, letterSpacing: '-0.02em' }}>PrepNus Board</div>
      <div style={{ marginTop: 16, fontSize: 18, color: '#9CA3AF' }}>
        {subtitle ?? 'phase-1 runtime · 22-slide demo'}
      </div>
      <div style={{ marginTop: 30, fontSize: 18, color: '#E8E8E8', opacity: 0.85 }}>
        Press <kbd style={kbd}>SPACE</kbd> or click to begin · <kbd style={kbd}>F</kbd> fullscreen
      </div>
      <div style={{ marginTop: 28, fontSize: 14, color: '#5C6B73', display: 'flex', gap: 18 }}>
        <a style={linkStyle} href="?mode=play">play mode</a>
        <a style={linkStyle} href="?mode=record">record mode</a>
        <a style={linkStyle} href="?">present mode</a>
      </div>
    </div>
  );
}

const splashStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: '#0F1923',
  color: '#F5B800',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  fontFamily: 'Inter, system-ui, sans-serif',
  cursor: 'pointer',
  userSelect: 'none',
};

const linkStyle: React.CSSProperties = {
  color: '#9CA3AF',
  textDecoration: 'none',
  borderBottom: '1px dashed rgba(156,163,175,0.4)',
  paddingBottom: 2,
};

const kbd: React.CSSProperties = {
  fontFamily: 'ui-monospace, monospace',
  fontSize: 16,
  padding: '2px 8px',
  border: '1px solid #F5B80060',
  borderRadius: 4,
  background: '#1A2733',
  color: '#F5B800',
  margin: '0 2px',
};

const touchZone: React.CSSProperties = {
  position: 'fixed',
  bottom: 0,
  width: '22vw',
  maxWidth: 180,
  height: '20vh',
  maxHeight: 180,
  background: 'transparent',
  border: 'none',
  padding: 0,
  margin: 0,
  cursor: 'pointer',
  zIndex: 10,
  WebkitTapHighlightColor: 'transparent',
  outline: 'none',
};
